import analytics from "web/script/analytics/analytics";
import baustein from "baustein";
import browser from "web/script/utils/browser";
import events from "web/script/dom/events";

import "web/script/components/accordions";
import "web/script/components/buybuybuy-option";
import "web/script/components/lead-link";

const name = "buybuybuy-options";

const selectors = {
    COUNT: `.${name}__count`,
    LOADER: `.${name}__loader`,
    SEARCH: `.${name}__search`,
    RESULTS: `.${name}__results`,
    TOOLTIPS: ".tooltip",
};

const states = {
    COLLAPSED: `${name}--collapsed`,
    SEARCHING: `${name}--searching`,
    LIMITLESS: `${name}--limitless`,
    RESULTS_PRE_ANIMATED: `${name}__results--pre-animated`,
    SEARCH_POST_ANIMATED: `${name}__search--post-animated`,
};

const FINAL_COUNT = 300 + Math.ceil(Math.random() * 150);

/**
 * NEW BUY OPTIONS IN NEW PDP
 *
 * This is part of a new iteration of our PDP.
 * Being a test, is not all the refined it could be.
 *
 * @TODO This component has been reverse engineered due to new PDP performing poorly,
 *       needs to be refactored.
 *
 * @see https://confluence.lystit.com/display/WEBCETERA/BuyBuyBuy+PDP
 *
 * The main responsibilities of this component are:
 * - displaying initially a search loading animation.
 * - displaying the buy options (via animation) once the search loading animation has finished.
 * - displaying buy options according to picked size.
 *
 * @see components/buybuybuy-area
 * @see components/buybuybuy-option
 * @see components/accordions
 * @see dom/events (onTransitionEnd, onAnimationEnd)
 */
export default baustein.register(name, {
    init() {
        /**
         * Communicates with components/buybuybuy-area whether the options are loaded or not.
         *
         * @public
         * @see components/buybuybuy-area
         * @type {boolean}
         */
        this.loaded = false;
    },

    setupEvents(add) {
        add("accordionOpened", this._onAccordionOpened);
        add("accordionClosed", this._onAccordionClosed);
    },

    onInsert() {
        // If there's no need to display via a search animation, try to render the Buy Options directly.
        if (!this.options.withSearch) {
            this._displayBuyOptions();
        }
    },

    /**
     * Starts displaying the Buy Options. This method is in charge of animating the height of the
     * component and triggering the start of the search animation.
     *
     * @public
     * @param {number} delay Delaying the beginning of the animation in ms.
     * @returns {undefined}
     */
    start(delay = 0) {
        const loadingEl = this.el.querySelector(selectors.SEARCH);

        window.setTimeout(() => {
            this.el.classList.add(states.SEARCHING);
            loadingEl.classList.remove("hidden");

            events
                .onTransitionEnd(
                    this.el,
                    () => {
                        this.el.classList.remove(states.COLLAPSED);
                    },
                    200,
                    "height"
                )
                .then(() => {
                    this._animateAndExpand();
                });
        }, delay);
    },

    /**
     * Assumes that the Buy Options are alreaady loaded and visisble, then hides them. Listens for
     * the fade-out animation to finish and once it does it shows a spinner, returning a promise.
     *
     * Freezes the height of the component to prevent jumps when computing the visible accordions
     * on components/buybuybuy-area.
     *
     * To use in conjunction with `showResults`.
     *
     * @public
     * @see components/buybuybuy-area#_computeAccordions
     * @returns {Promise}
     */
    hideResults() {
        const resultsEl = this.el.querySelector(selectors.RESULTS);

        return new Promise((resolve) => {
            requestAnimationFrame(() => {
                // freeze the height of the element to prevent jumps and reset overflow
                this.el.style.height = `${resultsEl.scrollHeight}px`;
                this.el.style.overflowY = null;

                events
                    .onTransitionEnd(resultsEl, fadeOut, 200, "opacity")
                    .then((el) => {
                        const loaderEl = this.el.querySelector(selectors.LOADER);

                        requestAnimationFrame(() => {
                            loaderEl.classList.remove("hidden");
                        });

                        resolve(el);
                    })
                    .catch(logError);
            });
        });

        function fadeOut() {
            resultsEl.classList.add(states.RESULTS_PRE_ANIMATED);
        }

        function logError() {
            console.error(`There was an error fading out ${resultsEl}.`);
        }
    },

    /**
     * Assumes that the Buy Options have just been hidden by `hideResults`.
     *
     * Expands a targeted accordion and, once done, animates the height of the component to
     * the height of the current Buy Options. After that, hides the loading spinner shown
     * by `hideResults` and fades-in the Buy Options.
     *
     * Accepts an optional delay so that the timing for the spinner shown to the user can be faked.
     * Unfreezes the height of the component set by `hideResults`.
     *
     * @public
     * @see hideResults()
     * @see components/accordions#expandTarget
     * @param {number} delay How much to delay showing the results.
     * @returns {Promise}
     */
    showResults(delay = 0) {
        const mainEl = this.el;
        const resultsEl = mainEl.querySelector(selectors.RESULTS);

        return new Promise((resolve) => {
            requestAnimationFrame(() => {
                events
                    .onTransitionEnd(mainEl, updateHeight.bind(this), 300 + delay, "height")
                    .then((el) => {
                        window.setTimeout(() => {
                            requestAnimationFrame(() => {
                                const loaderEl = el.querySelector(selectors.LOADER);
                                loaderEl.classList.add("hidden");
                                resultsEl.classList.remove(states.RESULTS_PRE_ANIMATED);

                                // Unfreeze element's height, and show overflowing elements
                                this.el.style.height = "auto";
                                this.el.style.overflowY = "visible";

                                resolve();
                            });
                        }, delay);
                    })
                    .catch(logError);
            });
        });

        function updateHeight() {
            mainEl.style.height = `${resultsEl.scrollHeight}px`;
        }

        function logError() {
            console.error(
                `There was an error listening for the height animation to finish on '${mainEl}'.`
            );
        }
    },

    /**
     * Called once start() is done, it's in charge of displaying an incremental search animation
     * and triggering the display of the Buy Options once it has finished.
     *
     * @returns {undefined}
     */
    _animateAndExpand() {
        const barEl = this.el.querySelector(`.${name}__bar`);
        const countEl = this.el.querySelector(selectors.COUNT);
        const eventName = browser.animationEndEvent;

        if (eventName) {
            events
                .onAnimationEnd(
                    barEl,
                    (bar) => {
                        bar.classList.add(`${name}__bar--loading`);
                        this._incrementCount(countEl);
                    },
                    null,
                    1400
                )
                .then(() => {
                    this._arrangeLayoutAfterSearch();
                    this._displayBuyOptions();
                });
        }
    },

    /**
     *  In charge of preparing the layout to display the available Buy Options once the search
     *  animation has finished. Consumed by _animateAndExpand.
     *
     * @see _animateAndExpand
     * @returns {undefined}
     */
    _arrangeLayoutAfterSearch() {
        requestAnimationFrame(() => {
            const searchEl = this.el.querySelector(selectors.SEARCH);
            const resultsEl = this.el.querySelector(selectors.RESULTS);

            searchEl.classList.add(states.SEARCH_POST_ANIMATED);
            resultsEl.classList.remove("hidden");
            searchEl.classList.add("hidden");
        });
    },

    /**
     * Consumed by _animateAndExpand() and onInsert().
     * It's in charge of animating the height of the component to accommodate the Buy Options
     * and notify components/buybuybuy-area about it.
     *
     * @see _animateAndExpand
     * @returns {undefined}
     */
    _displayBuyOptions() {
        this.emit("buyOptionsReady");

        this.showResults().then(() => {
            requestAnimationFrame(() => {
                if (this.options.withSearch) {
                    if (!this.loaded) {
                        this.el.classList.remove(states.SEARCHING);
                    }

                    this.emit("buyOptionsLoaded");
                }

                this.loaded = true;
            });
        });
    },

    /**
     * Method in charge of creating an incremental count animation used during _animateAndExpand().
     *
     * @param {HTMLElement} el The HTML element to update on each iteration.
     * @returns {undefined}
     */
    _incrementCount(el) {
        const newCount = Math.ceil(parseFloat(el.textContent, 10) + Math.random() * 10);

        if (newCount >= FINAL_COUNT) {
            el.innerHTML = FINAL_COUNT;
            return;
        }

        el.innerHTML = newCount;

        requestAnimationFrame(this._incrementCount.bind(this, el));
    },

    /**
     * Sends analyctics once any of the Buy Options is expanded.
     *
     * @param {Object} event Baustein's wrapper around DOM event object.
     * @returns {undefined}
     */
    _onAccordionOpened(event) {
        const { accordionName, nonInteraction } = event;
        analytics.event("buy_area", "open_option", accordionName, nonInteraction);
    },

    /**
     * Sends analyctics once any of the Buy Options is collapsed.
     *
     * @param {Object} event: Baustein's wrapper around DOM event object.
     * @returns {undefined}
     */
    _onAccordionClosed(event) {
        const { accordionName } = event;
        analytics.event("buy_area", "close_option", accordionName);
    },
});
